import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosService from '../services/axios.service';
import { Badge, BadgeRequest, BadgeAward, BadgeRecipientDTO, CreateBadgeRequestDto } from '../../../shared/types';
import { useCrewModuleEnabled } from './useCrews';

// API functions
const getBadges = async (crewSlug: string) => {
    const { data: responseData } = await axiosService.get(`/c/${crewSlug}/badges`);
    return responseData.data;
};

const getBadgeRequests = async (crewSlug: string) => {
    const { data: responseData } = await axiosService.get(`/c/${crewSlug}/badges/requests`);
    return responseData.data;
};

const getBadgeAwards = async (crewSlug: string, profileId?: string) => {
    const params = profileId ? { profileId } : {};
    const { data: responseData } = await axiosService.get(`/c/${crewSlug}/badges/awards`, { params });
    return responseData.data;
};

const createBadge = async (crewSlug: string, data: Partial<Badge>) => {
    const { data: responseData } = await axiosService.post(`/c/${crewSlug}/badges`, data);
    return responseData.data;
};

const updateBadge = async (crewSlug: string, badgeId: string, data: Partial<Badge>) => {
    const { data: responseData } = await axiosService.put(`/c/${crewSlug}/badges/b/${badgeId}`, data);
    return responseData.data;
};

const awardBadge = async (crewSlug: string, data: { badgeId: string; profileId: string; reason?: string }) => {
    const { data: responseData } = await axiosService.post(`/c/${crewSlug}/badges/award`, data);
    return responseData.data;
};

const createBadgeRequest = async (crewSlug: string, data: CreateBadgeRequestDto) => {
    const { data: responseData } = await axiosService.post(`/c/${crewSlug}/badges/requests`, data);
    return responseData.data;
};

const reviewBadgeRequest = async (crewSlug: string, requestId: string, review: { status: 'approved' | 'rejected'; rejectionReason?: string }) => {
    const { data: responseData } = await axiosService.post(`/c/${crewSlug}/badges/requests/${requestId}/review`, review);
    return responseData.data;
};

const deleteBadge = async (crewSlug: string, badgeId: string) => {
    const { data: responseData } = await axiosService.delete(`/c/${crewSlug}/badges/b/${badgeId}`);
    return responseData.data;
};

// Custom hook
export const useBadges = (crewSlug?: string) => {
    const queryClient = useQueryClient();
    const badgesEnabled = useCrewModuleEnabled(crewSlug ?? '', 'badges');

    // Queries
    const {
        data: badges = [],
        isLoading: badgesLoading,
        error: badgesError
    } = useQuery<Badge[]>({
        queryKey: ['badges', crewSlug],
        queryFn: () => getBadges(crewSlug!),
        enabled: !!crewSlug && badgesEnabled
    });

    const {
        data: badgeRequests = [],
        isLoading: badgeRequestsLoading,
        error: badgeRequestsError
    } = useQuery<BadgeRequest[]>({
        queryKey: ['badgeRequests', crewSlug],
        queryFn: () => getBadgeRequests(crewSlug!),
        enabled: !!crewSlug && badgesEnabled
    });

    const {
        data: badgeAwards = [],
        isLoading: badgeAwardsLoading,
        error: badgeAwardsError
    } = useQuery<BadgeAward[]>({
        queryKey: ['badgeAwards', crewSlug],
        queryFn: () => getBadgeAwards(crewSlug!),
        enabled: !!crewSlug && badgesEnabled
    });

    // Mutations

    // Mutations
    const createBadgeMutation = useMutation({
        mutationFn: (data: Partial<Badge>) => createBadge(crewSlug!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['badges', crewSlug] });
        }
    });

    const updateBadgeMutation = useMutation({
        mutationFn: ({ badgeId, data }: { badgeId: string; data: Partial<Badge> }) =>
            updateBadge(crewSlug!, badgeId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['badges', crewSlug] });
        }
    });

    const awardBadgeMutation = useMutation({
        mutationFn: (data: { badgeId: string; profileId: string; reason?: string }) => awardBadge(crewSlug!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['badgeAwards', crewSlug] });
        }
    });

    const createBadgeRequestMutation = useMutation({
        mutationFn: (data: CreateBadgeRequestDto) => createBadgeRequest(crewSlug!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['badgeRequests', crewSlug] });
        }
    });

    const reviewBadgeRequestMutation = useMutation({
        mutationFn: ({ requestId, review }: { requestId: string; review: { status: 'approved' | 'rejected'; rejectionReason?: string } }) =>
            reviewBadgeRequest(crewSlug!, requestId, review),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['badgeRequests', crewSlug] });
            queryClient.invalidateQueries({ queryKey: ['badges', crewSlug] });
        }
    });

    const deleteBadgeMutation = useMutation({
        mutationFn: (badgeId: string) => deleteBadge(crewSlug!, badgeId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['badges', crewSlug] });
            queryClient.invalidateQueries({ queryKey: ['badgeAwards', crewSlug] });
        }
    });

    const isLoading = badgesLoading || badgeRequestsLoading || badgeAwardsLoading;
    const error = badgesError || badgeRequestsError || badgeAwardsError;

    return {
        // Data
        badges,
        badgeRequests,
        badgeAwards,

        // Loading states
        isLoading,
        badgesLoading,
        badgeRequestsLoading,
        badgeAwardsLoading,

        // Errors
        error,
        badgesError,
        badgeRequestsError,
        badgeAwardsError,

        // Mutations
        createBadge: createBadgeMutation.mutate,
        updateBadge: updateBadgeMutation.mutate,
        awardBadge: awardBadgeMutation.mutate,
        createBadgeRequest: createBadgeRequestMutation.mutate,
        reviewBadgeRequest: reviewBadgeRequestMutation.mutate,
        deleteBadge: deleteBadgeMutation.mutate,

        // Mutation states
        createBadgeMutation,
        updateBadgeMutation,
        awardBadgeMutation,
        createBadgeRequestMutation,
        reviewBadgeRequestMutation,
        deleteBadgeMutation
    };
};

// Hook for fetching badges for a specific profile
export const useProfileBadges = (crewSlug: string, profileId: string) => {
    const badgesEnabled = useCrewModuleEnabled(crewSlug, 'badges');
    return useQuery<BadgeAward[]>({
        queryKey: ['profileBadges', crewSlug, profileId],
        queryFn: () => getBadgeAwards(crewSlug, profileId),
        enabled: !!crewSlug && !!profileId && badgesEnabled
    });
};

// API function to get a single badge by ID
const getBadgeById = async (crewSlug: string, badgeId: string) => {
    const { data: responseData } = await axiosService.get(`/c/${crewSlug}/badges/b/${badgeId}`);
    return responseData.data;
};

// API function to get paginated badge recipients
const getBadgeRecipients = async (crewSlug: string, badgeId: string, page: number = 1, limit: number = 20) => {
    const { data: responseData } = await axiosService.get(`/c/${crewSlug}/badges/b/${badgeId}/recipients`, {
        params: { page, limit }
    });
    return {
        recipients: responseData.data,
        pagination: responseData.pagination
    };
};

// Hook for fetching a single badge by ID
export const useBadge = (crewSlug: string, badgeId: string) => {
    return useQuery<Badge>({
        queryKey: ['badge', crewSlug, badgeId],
        queryFn: () => getBadgeById(crewSlug, badgeId),
        enabled: !!crewSlug && !!badgeId
    });
};

// Hook for fetching paginated badge recipients
export const useBadgeRecipients = (crewSlug: string, badgeId: string, page: number = 1, limit: number = 20) => {
    return useQuery<{
        recipients: BadgeRecipientDTO[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    }>({
        queryKey: ['badgeRecipients', crewSlug, badgeId, page, limit],
        queryFn: () => getBadgeRecipients(crewSlug, badgeId, page, limit),
        enabled: !!crewSlug && !!badgeId,
        placeholderData: (previousData) => previousData
    });
};

// Standalone hook for awarding a badge without loading all badges
export const useAwardBadge = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: { badgeId: string; profileId: string; reason?: string }) => awardBadge(crewSlug, data),
        onSuccess: (_, variables) => {
            // Invalidate badge awards queries
            queryClient.invalidateQueries({ queryKey: ['badgeAwards', crewSlug] });
            // Invalidate the specific badge query to update awardedCount
            queryClient.invalidateQueries({ queryKey: ['badge', crewSlug, variables.badgeId] });
            // Invalidate badge recipients to show the new award
            queryClient.invalidateQueries({ queryKey: ['badgeRecipients', crewSlug, variables.badgeId] });
        }
    });
};
